import { Filter, PlusCircle } from "feather-icons-react/build/IconComponents";
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { all_routes } from "../../Router/all_routes";
import Table from "../../core/pagination/datatable";
import useProducts from "../../hooks/useProducts";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import EditProduct from "../../core/modals/inventory/EditProduct";
import { deleteProduct } from "../../api/inventoryApi";
import { removeProduct } from "../../slices/productSlice";
import { useDispatch } from "react-redux";

const ProductList = () => {
  const { products, categoryData } = useProducts();
  const route = all_routes;
  const dispatch = useDispatch();

  // États pour la gestion des filtres
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null); 

  // Préparer les options de catégorie et de stock
  const categoryOptions = categoryData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const stockOptions = [
    { value: "Bar", label: "Bar" },
    { value: "Exposition", label: "Exposition" },
    { value: "Cuisine", label: "Cuisine" },
    { value: "Stock général", label: "Stock général" },
  ];

  // 🔥 Filtres combinés : Recherche par nom + Catégorie + Stock
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesName = searchTerm ? product.name.toLowerCase().includes(searchTerm.toLowerCase()) : true;
      const matchesCategory = selectedCategory ? product.category?.id === selectedCategory.value : true;
      const matchesStock = selectedStock ? product.stock === selectedStock.value : true;
      return matchesName && matchesCategory && matchesStock;
    });
  }, [products, searchTerm, selectedCategory, selectedStock]);

  // Gestion du toggle du filtre
  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

  const deleteFunction = async (productId) => {
    try {
      const response = await deleteProduct(productId);
  
      if (response?.error) {
        // Gestion des erreurs retournées par le serveur
        Swal.fire("Erreur", response.error.message || "Une erreur est survenue.", "error");
      } else {
        Swal.fire("Supprimé !", "Votre produit a été supprimé avec succès.", "success");
      }
    } catch (error) {
      // Gestion des erreurs inattendues (réseau, serveur non joignable, etc.)
      Swal.fire(
        "Erreur",
        error?.response?.data?.message || "Une erreur imprévue est survenue.",
        "error"
      );
    }
  };
  const columns = [
    { title: "Créé par", dataIndex: "user", render: (user) => user?.name || "Non défini" },
    { title: "Stock", dataIndex: "stock", key: "stock" },
    { title: "Produit", dataIndex: "name", key: "name" },
    { title: "Catégorie", dataIndex: "category", render: (category) => category?.name || "Non défini" },
    {
      title: "Quantité",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.quantite}</strong>
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Qte alerte",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.alerte_quantite}</strong>
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Prix de vente",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.prix_vente}</strong> XOF
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Prix d'achat",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.prix_achat}</strong> XOF
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    
    {
      title: "Action",
      dataIndex: "action",
      key: "actions",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
          
            <button
              className="btn btn-sm btn-primary me-2"
              onClick={() => handleEdit(record)}
              data-bs-toggle="modal" data-bs-target="#edit-stock"
            >
              <i data-feather="edit" className="feather-edit"></i>
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => handleDelete(record.id)}
            >

              <i data-feather="Trash2" className="feather-trash-2"></i>
            </button>
          </div>
        </div>
      ),
    },
  ];

  // Gestion de l'ouverture de l'éditeur
  const handleEdit = (product) => {

    setSelectedProduct(product);

    setIsEditModalVisible(true);
    
  };
  const handleDelete = (id) => {
    Swal.fire({
      title: "Êtes-vous sûr?",
      text: "Cette action est irréversible!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Oui, supprimer!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then((result) => {
      if (result.isConfirmed) {
        
        dispatch(removeProduct(id)); 
        
        deleteFunction(id);
      }
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Liste des Produits</h4>
              <h6>Gérez vos produits</h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to={route.addproduct} className="btn btn-added">
              <PlusCircle className="me-2 iconsize" />
              Ajouter un produit
            </Link>
           
          </div>
          <div className="page-btn">
           
            <Link to={route.stockmove} className="btn btn-added">
              <PlusCircle className="me-2 iconsize" />
              Enregistrer un mouvement
            </Link>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Rechercher un produit..."
                    className="form-control form-control-sm formsearch"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="search-path">
                <button className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`} >
                <Filter
                    className="filter-icon"
                    onClick={toggleFilterVisibility}
                  />
                  <span onClick={toggleFilterVisibility}>
                    <ImageWithBasePath
                      src="assets/img/icons/closes.svg"
                      alt="img"
                    />
                  </span>                
                  </button>
              </div>
            </div>

            {isFilterVisible && (
              <div className="card filter-card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-4">
                      <label>Catégorie</label>
                      <Select options={categoryOptions} value={selectedCategory} onChange={setSelectedCategory} placeholder="Choisir une catégorie" />
                    </div>
                    <div className="col-lg-4">
                      <label>Stock</label>
                      <Select options={stockOptions} value={selectedStock} onChange={setSelectedStock} placeholder="Choisir un stock" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="table-responsive">
              <Table columns={columns} dataSource={filteredProducts} />
            </div>
          </div>
        </div>
      </div>
      {isEditModalVisible && (
        <EditProduct
          product={selectedProduct}
          onClose={() => setIsEditModalVisible(false)}
        />
      )}
    </div>
  );
};


export default ProductList;
